import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { FaCopy, FaEdit, FaFilter, FaSearch, FaShareAlt, FaTable, FaTimes } from "react-icons/fa";
import BackButton from "../../components/navigation/BackButton";
import Spinner from "../../components/Spinner";
import { LdsOverviewHero, LdsOverviewPage } from "../../components/lds/LdsOverview";
import { useCan } from "../../authz/useCan";
import { createReport, fetchCatalog, fetchReport, runReport, runSavedReport, shareReport } from "./api";
import ExportButtons from "./components/ExportButtons";
import FilterBar from "./components/FilterBar";
import ResultTable from "./components/ResultTable";
import SummaryResult from "./components/SummaryResult";
import { actionBar, ghostButton, input, primaryButton, secondaryButton } from "./components/styles";
import { MAX_CONDITIONS, combineFilters, conditionCount, searchGroup, searchablePaths, withoutUnavailable } from "./definition";
import { reportErrorMessage } from "./errors";
import { summarySentence } from "./labels";
import { useReportRun } from "./useReportRun";
import type { Catalog, FilterGroupSpec, ReportSpec, SavedReport } from "./types";

const PAGE_SIZE = 50;
const SEARCH_DELAY_MS = 400;
const EMPTY_FILTER: FilterGroupSpec = { match: "ALL", conditions: [] };

/** A saved report (own or from the library), run with the permissions of whoever opens it. */
export default function ReportViewPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const canEdit = useCan("reports.builder", "create");
  const canShare = useCan("reports.builder", "share");
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [report, setReport] = useState<SavedReport | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Filters added while viewing: not saved, and not part of Excel or Print (those use the saved report). */
  const [extraFilter, setExtraFilter] = useState<FilterGroupSpec>(EMPTY_FILTER);
  /** What is typed in the search box, and the same text once typing pauses (that one runs the report). */
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const filterArea = useRef<HTMLDivElement>(null);
  const reportRun = useReportRun(t);
  const { run } = reportRun;

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchCatalog(), fetchReport(id)])
      .then(([loadedCatalog, loadedReport]) => {
        if (cancelled) return;
        setCatalog(loadedCatalog);
        setReport(loadedReport);
        run((page, signal) => runSavedReport(id, page, PAGE_SIZE, signal), 0);
      })
      .catch((caught) => { if (!cancelled) setLoadError(reportErrorMessage(t, caught)); });
    return () => { cancelled = true; };
  }, [id, run, t]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchText.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  // The filter panel closes on a click elsewhere or on Escape.
  useEffect(() => {
    if (!filterOpen) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (filterArea.current && !filterArea.current.contains(event.target as Node)) setFilterOpen(false);
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setFilterOpen(false); };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [filterOpen]);

  /** The view's own additions: the extra conditions plus the search (one "at least one of" group). */
  const viewExtra = useMemo<FilterGroupSpec>(() => {
    if (!report) return EMPTY_FILTER;
    const room = MAX_CONDITIONS - conditionCount(report.definition.filter) - extraFilter.conditions.length;
    const searching = catalog ? searchGroup(catalog, report.definition, search, room) : null;
    const conditionsAll = extraFilter.match === "ALL" || extraFilter.conditions.length <= 1;
    return {
      match: "ALL",
      conditions: conditionsAll ? extraFilter.conditions : [],
      groups: [
        ...(conditionsAll ? [] : [{ match: "ANY" as const, conditions: extraFilter.conditions }]),
        ...(searching ? [searching] : []),
      ],
    };
  }, [catalog, report, extraFilter, search]);

  const viewed = useMemo<ReportSpec | null>(() => {
    if (!report) return null;
    const filter = combineFilters(report.definition.filter, viewExtra);
    return filter ? { ...report.definition, filter } : null;
  }, [report, viewExtra]);
  const filtering = viewExtra.conditions.length > 0 || (viewExtra.groups ?? []).length > 0;
  const canAddFilters = useMemo(() => Boolean(report) && combineFilters(report!.definition.filter,
    { match: "ALL", conditions: [{ path: "", operator: "IS_EMPTY" }] }) !== null, [report]);
  const canSearch = canAddFilters && Boolean(catalog && report && searchablePaths(catalog, report.definition).length > 0);

  /** Without extra filters the saved report itself runs (GET, also during view-as); with them, the combination. */
  const runPage = useCallback((page: number) => {
    if (!report) return;
    if (!filtering || !viewed) {
      run((p, signal) => runSavedReport(report.id, p, PAGE_SIZE, signal), page);
    } else {
      run((p, signal) => runReport(viewed, p, PAGE_SIZE, signal), page);
    }
  }, [filtering, report, run, viewed]);

  // Apply added or removed filters straight away (the first run happens when the report is loaded).
  const firstFilter = useRef(true);
  useEffect(() => {
    if (firstFilter.current) {
      firstFilter.current = false;
      return;
    }
    runPage(0);
    // Only when the extra filters or the search change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraFilter, search]);

  const toggleShare = async () => {
    if (!report || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      setReport(await shareReport(report.id, !report.shared));
    } catch (caught) {
      setActionError(reportErrorMessage(t, caught));
    } finally {
      setBusy(false);
    }
  };

  /** A copy of a colleague's report becomes your own; parts you cannot use are left out. */
  const copy = async () => {
    if (!report || !catalog || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      const created = await createReport({
        name: String(t("reports.builder.copy_name", { name: report.name, defaultValue: "Copy of {{name}}" })).slice(0, 120),
        description: report.description,
        definition: withoutUnavailable(catalog, report.definition).definition,
      });
      navigate(`/reports/builder/${created.id}/edit`);
    } catch (caught) {
      setActionError(reportErrorMessage(t, caught));
    } finally {
      setBusy(false);
    }
  };

  const back = <div><BackButton fallback="/reports/builder" /></div>;

  if (loadError) {
    return (
      <LdsOverviewPage>
        <p role="alert" className="text-sm text-rose-700 dark:text-rose-200">{loadError}</p>
        {back}
      </LdsOverviewPage>
    );
  }
  if (!catalog || !report) {
    return <LdsOverviewPage><div className="flex justify-center py-10"><Spinner /></div></LdsOverviewPage>;
  }

  const subtitle = summarySentence(t, catalog, report.definition);
  const madeBy = !report.owned && report.ownerName
    ? ` · ${t("reports.builder.made_by", { name: report.ownerName, defaultValue: "Made by {{name}}" })}`
    : "";

  return (
    <LdsOverviewPage>
      {back}
      <LdsOverviewHero
        icon={FaTable}
        eyebrow={String(report.shared
          ? t("reports.builder.library", { defaultValue: "Library" })
          : t("reports.builder.title", { defaultValue: "Report builder" }))}
        title={report.name}
        description={`${report.description || subtitle}${madeBy}`}
      />
      <div className={actionBar}>
        <ExportButtons catalog={catalog} definition={report.definition} reportId={report.id} name={report.name}
                       subtitle={subtitle} onError={setActionError} />
        {canShare && report.owned && (
          <button type="button" className={secondaryButton} disabled={busy} onClick={toggleShare}>
            <FaShareAlt aria-hidden /> {report.shared
              ? t("reports.builder.unshare", { defaultValue: "Stop sharing" })
              : t("reports.builder.share", { defaultValue: "Share in library" })}
          </button>
        )}
        {canEdit && !report.owned && (
          <button type="button" className={secondaryButton} disabled={busy} onClick={copy}>
            <FaCopy aria-hidden /> {t("reports.builder.make_copy", { defaultValue: "Make a copy" })}
          </button>
        )}
        {canEdit && report.owned && (
          <button type="button" className={primaryButton} onClick={() => navigate(`/reports/builder/${id}/edit`)}>
            <FaEdit aria-hidden /> {t("reports.builder.edit", { defaultValue: "Edit" })}
          </button>
        )}
        <div className="ml-auto flex min-w-0 flex-wrap items-center gap-2">
          {canSearch && (
            <label className="relative block w-full sm:w-64">
              <span className="sr-only">{t("reports.builder.search_label", { defaultValue: "Search in this report" })}</span>
              <FaSearch aria-hidden className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400" />
              <input
                type="search"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder={String(t("reports.builder.search_placeholder", { defaultValue: "Search…" }))}
                className={`${input} pl-8`}
              />
            </label>
          )}
          <div ref={filterArea} className="relative">
            <button
              type="button"
              className={`${secondaryButton} ${extraFilter.conditions.length > 0 ? "border-indigo-300 text-indigo-700 dark:text-indigo-200" : ""}`}
              aria-expanded={filterOpen}
              aria-haspopup="dialog"
              disabled={!canAddFilters}
              title={canAddFilters ? undefined : String(t("reports.builder.view_filters_unavailable", {
                defaultValue: "This report's own conditions cannot be combined with extra filters. Make a copy to adjust it.",
              }))}
              onClick={() => setFilterOpen((open) => !open)}
            >
              <FaFilter aria-hidden /> {t("reports.builder.filter", { defaultValue: "Filter" })}
              {extraFilter.conditions.length > 0 && (
                <span className="rounded-full bg-indigo-600 px-2 text-xs text-white dark:bg-indigo-500">
                  {extraFilter.conditions.length}
                </span>
              )}
            </button>
            {filterOpen && canAddFilters && (
              <div
                role="dialog"
                aria-label={String(t("reports.builder.view_filters", { defaultValue: "Filter this view" }))}
                className="absolute right-0 z-30 mt-2 w-[min(32rem,calc(100vw-2rem))] space-y-3 rounded-[22px] border border-slate-200 bg-white p-4 shadow-xl dark:border-white/10 dark:bg-slate-900"
              >
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t("reports.builder.view_filters", { defaultValue: "Filter this view" })}
                  </h2>
                  {extraFilter.conditions.length > 0 && (
                    <button type="button" className={ghostButton} onClick={() => setExtraFilter(EMPTY_FILTER)}>
                      <FaTimes aria-hidden /> {t("reports.builder.clear_filters", { defaultValue: "Clear filters" })}
                    </button>
                  )}
                </div>
                <FilterBar
                  catalog={catalog}
                  definition={{ ...report.definition, filter: extraFilter }}
                  schoolYearId={report.definition.context.schoolYear.mode === "FIXED"
                    ? report.definition.context.schoolYear.id ?? null
                    : catalog.currentSchoolYearId ?? null}
                  showGroups={false}
                  onChange={(next) => setExtraFilter({ match: next.filter.match, conditions: next.filter.conditions })}
                />
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t("reports.builder.view_filters_hint", {
                    defaultValue: "Only for this view: not saved, and Excel and Print use the report as saved.",
                  })}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {report.shared && report.owned && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t("reports.builder.shared_note", {
            defaultValue: "Shared in the library. Colleagues only see the data they have access to themselves.",
          })}
        </p>
      )}
      {filtering && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t("reports.builder.filtered_note", { defaultValue: "Filtered view: Excel and Print use the report as saved." })}
        </p>
      )}
      {actionError && <p role="alert" className="text-sm text-rose-700 dark:text-rose-200">{actionError}</p>}
      {reportRun.error && <p role="alert" className="text-sm text-rose-700 dark:text-rose-200">{reportRun.error}</p>}
      {reportRun.result && report.definition.summary ? (
        <SummaryResult
          catalog={catalog}
          definition={viewed ?? report.definition}
          result={reportRun.result}
          loading={reportRun.loading}
          onPage={runPage}
        />
      ) : reportRun.result ? (
        <ResultTable
          catalog={catalog}
          definition={viewed ?? report.definition}
          result={reportRun.result}
          loading={reportRun.loading}
          onPage={runPage}
        />
      ) : reportRun.loading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : null}
    </LdsOverviewPage>
  );
}
